import { RARITIES, type Rarity } from '../rarity';
import {
  FISH_SPECIES_LIST,
  findFishSpecies,
  RETIRED_FISH_SPECIES,
  type RetiredFishSpecies,
} from './fish';

/**
 * **Quanto vale um peixe, e em UM sítio só.**
 *
 * ## Porque isto é um ficheiro próprio
 *
 * Porque tem dois consumidores que **têm** de concordar: a cotação que o painel
 * mostra antes de o jogador carregar em `VENDER`, e a liquidação que credita o
 * Ouro. Se fossem duas contas, o preço mostrado e o preço pago acabariam por
 * divergir — e o jogador teria razão em chamar-lhe roubo.
 *
 * A função é **pura**: ela recebe uma espécie e um peso e devolve um número.
 * Não lê a base de dados, não conhece contas, não credita nada e não sabe o que
 * é Ouro seguro ou Ouro de campo.
 *
 * ## A aritmética
 *
 * ```
 *          valor-base × multiplicador da raridade × peso
 * preço = ───────────────────────────────────────────────
 *                 10 000 × peso de referência
 * ```
 *
 * Tudo em `bigint`, tudo multiplicado antes de se dividir, **uma truncagem só**,
 * e um piso de um Ouro. É a mesma forma que a vida de um peixe já usa, e pela
 * mesma razão: dividir em passos truncaria em cada um, e um exemplar pesado
 * perderia a diferença que o peso dele devia valer.
 *
 * As três propriedades que o contrato exige saem daqui por construção:
 *
 * - **mais peso nunca vale menos**, na mesma espécie, porque o peso é um fator
 *   do numerador e a divisão é monótona;
 * - os **multiplicadores crescem estritamente** com o nível da raridade, o que
 *   é afirmado pela ordem em que estão escritos e verificado ao carregar;
 * - **nunca zero**: um exemplar minúsculo de uma espécie comum vale um Ouro.
 *
 * Nenhum ponto flutuante decide nada.
 *
 * ## A curva, e o que continua por medir
 *
 * Os valores-base e os multiplicadores são a curva **B — Equilibrada**,
 * **aprovada pelo dono** no gate V28I.W depois da auditoria económica V28I.V. A
 * forma estava decidida desde o início; agora a curva também está.
 *
 * O que continua por medir, e está em `docs/OPEN_QUESTIONS.md`: o rendimento
 * real por hora, que não depende só desta tabela — depende da distribuição de
 * raridade da água, da reposição de um peixe a cada três minutos e do tempo de
 * captura, e nenhuma dessas três foi calibrada.
 */

/**
 * **A versão da tabela de preços.**
 *
 * Ela viaja no recibo. Um jogador que venda hoje e outro que venda depois de uma
 * revisão sabem, os dois, sob que tabela venderam — e uma reclamação sobre um
 * preço passa a ser uma pergunta com resposta em vez de uma discussão.
 *
 * Subi-la é uma decisão: ela não muda sozinha ao afinar um número, e afinar um
 * número sem a subir é esconder que a economia mudou.
 *
 * **3** desde o gate V28I.BK, que trocou as vinte espécies pelas vinte e sete
 * definitivas. Os valores-base de todas mudaram, e um recibo tem de dizer sob
 * que tabela vendeu.
 *
 * A **2** foi o gate V28I.W: a tabela 1 era provisória e ia até cerca de 186
 * milhões por exemplar — uma auditoria (V28I.V) mostrou que pagava a raridade
 * duas vezes e que uma única captura valia dezenas de milhares de horas de
 * caça. Foi substituída pela curva **B — Equilibrada**, aprovada pelo dono,
 * **antes de qualquer implantação**: nenhuma venda foi feita sob a tabela 1 em
 * lado nenhum.
 *
 * As **vinte espécies retiradas mantêm o valor-base que tinham**. A versão sobe
 * porque a tabela mudou de conteúdo, e não porque o preço delas tenha mudado —
 * uma captura antiga vale hoje exatamente o que valia ontem.
 *
 * Um recibo antigo continua a dizer a verdade: a versão e o Ouro de cada venda
 * são gravados na linha (`sold_price_version`, `sold_gold`), e nada aqui
 * reavalia um peixe já vendido.
 */
export const FISH_PRICE_VERSION = 3;

/** O denominador dos multiplicadores. Dez mil é um. */
export const FISH_PRICE_DENOMINATOR_BPS = 10_000n;

/**
 * **Quanto cada raridade multiplica o preço**, em basis points.
 *
 * Uma tabela TOTAL sobre `Rarity`: uma raridade nova é um erro de compilação
 * aqui, e não uma raridade cujo peixe vale zero.
 *
 * Cresce mais depressa do que a tabela de vida, e é deliberado: aguentar mais
 * golpes é uma inconveniência, e valer mais é a recompensa por a ter apanhado.
 *
 * ## De `x200` para `x50` (gate V28I.W)
 *
 * A tabela 1 multiplicava por **duzentos** e os valores-base subiam por
 * cinquenta e um mil — a raridade era paga **duas vezes**, e o produto dava nove
 * milhões de vezes entre um Common e um Prismatic de referência. A curva B
 * corta o multiplicador para **cinquenta** e a base para cerca de dois mil, e o
 * produto passa a **oitenta e nove mil**.
 *
 * A escada é aproximadamente geométrica, de razão `~1,5` por degrau: cada
 * raridade vale metade outra vez mais do que a anterior, sem um salto que
 * transforme um degrau numa economia diferente.
 *
 * **Aprovados pelo dono** (curva B), e já não provisórios na forma: o que
 * continua por medir é o rendimento real por hora, que depende da distribuição
 * da água e do tempo de captura — ver `docs/OPEN_QUESTIONS.md`.
 */
export const FISH_RARITY_PRICE_BPS: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 10_000,
  uncommon: 15_000,
  rare: 23_000,
  epic: 35_000,
  mythic: 53_000,
  legendary: 80_000,
  excellent: 121_000,
  perfect: 184_000,
  anomaly: 280_000,
  prismatic: 500_000,
});

/**
 * **O valor de um exemplar de referência de cada espécie**, em Ouro.
 *
 * Por espécie e não por raridade, porque duas espécies da mesma raridade não
 * têm o mesmo tamanho: uma Baleia Gravítica de referência pesa cento e cinquenta
 * quilos e uma Serpente de Brasa cento e trinta, e o preço tem de significar a
 * mesma coisa nas duas.
 *
 * Uma tabela por id, verificada ao carregar contra o catálogo: uma espécie sem
 * preço é um erro no arranque, e não um peixe que vale um Ouro em silêncio.
 *
 * ## A curva B, e o que cada linha vale (gate V28I.W)
 *
 * O preço de um exemplar de referência é `base x multiplicador`, e é ele que
 * ancora a escada inteira:
 *
 * | raridade | posições | referência | máximo da banda |
 * | --- | --- | ---: | ---: |
 * | common | 01–03 | 6 / 9 / 15 | 38 |
 * | uncommon | 04–06 | 22 / 36 / 55 | 144 |
 * | rare | 07–09 | 87 / 135 / 211 | 552 |
 * | epic | 10–12 | 329 / 525 / 805 | 2 098 |
 * | mythic | 13–15 | 1 272 / 1 961 / 3 074 | 7 992 |
 * | legendary | 16–18 | 4 800 / 7 440 / 12 000 | 31 200 |
 * | excellent | 19–21 | 18 150 / 27 830 / 44 770 | 116 402 |
 * | perfect | 22–23 | 69 920 / 108 560 | 279 624 |
 * | anomaly | 24–25 | 168 000 / 263 200 | 684 320 |
 * | prismatic | 26–27 | 410 000 / 650 000 | **1 697 222** |
 *
 * O maior exemplar possível do jogo vale **1,70 milhões** — abaixo dos 1,8 da
 * tabela 2 e longe dos 186 da tabela 1. É uma noite excecional, e não uma vida
 * resolvida: pelas despesas que existem, é uma tiragem de dez do gacha, ou uma
 * progressão completa de equipamento — grande, memorável, e absorvível.
 *
 * A escada é uma **progressão geométrica de 27 passos** entre os mesmos
 * extremos que a curva B já tinha (6 Ouro na posição 1, 640 000 na 27), com um
 * passo de aproximadamente **1,51x** por posição. Os valores-base saem dela por
 * divisão pelo multiplicador da raridade, e não o contrário — é isso que torna a
 * monotonia por posição uma consequência da construção e não uma coincidência.
 *
 * **Aprovados pelo dono.** As faixas de peso, as vidas e a distribuição da água
 * não foram tocadas por este ficheiro.
 */
const BASE_VALUE_BY_SPECIES: Readonly<Record<string, bigint>> = Object.freeze({
  /* common */
  fish_tambaqui: 6n,
  fish_pirarucu: 9n,
  fish_baiacu: 15n,
  /* uncommon */
  fish_space_frog: 15n,
  fish_all_seeing_serpent: 24n,
  fish_rainbow: 37n,
  /* rare */
  fish_angel: 38n,
  fish_monster: 59n,
  fish_fire: 92n,
  /* epic */
  fish_strange: 94n,
  fish_eel: 150n,
  fish_demon: 230n,
  /* mythic */
  fish_abyssal_chimera: 240n,
  fish_whale: 370n,
  fish_mosasaur: 580n,
  /* legendary */
  fish_titanoboa: 600n,
  fish_baphomet: 930n,
  fish_megalodon: 1_500n,
  /* excellent */
  fish_baby_megalodon: 1_500n,
  fish_golden_megalodon: 2_300n,
  fish_golden_baby_megalodon: 3_700n,
  /* perfect */
  fish_black_megalodon: 3_800n,
  fish_colossal_squid: 5_900n,
  /* anomaly */
  fish_kraken: 6_000n,
  fish_devil_dragon: 9_400n,
  /* prismatic */
  fish_abyssal_titanic: 8_200n,
  fish_lord_poseidon: 13_000n,

  /*
   * **As vinte retiradas** (gate V28I.BK). Elas saíram do sorteio e não do
   * jogo: uma captura em `account_catches` continua a valer exatamente o que
   * valia. Apagá-las daqui fá-las-ia cair no piso de um Ouro — 5 440 de uma
   * Baleia Gravítica passariam a 1 —, que é confisco com outro nome.
   */
  fish_silt_larva: 6n,
  fish_glass_minnow: 8n,
  fish_ion_eel: 16n,
  fish_dust_ray: 19n,
  fish_tidal_lantern: 38n,
  fish_hollow_carp: 44n,
  fish_ferrite_moray: 110n,
  fish_pressure_jaw: 125n,
  fish_void_angler: 280n,
  fish_nebula_koi: 310n,
  fish_gravity_whale: 680n,
  fish_ember_serpent: 740n,
  fish_chrome_marlin: 1_200n,
  fish_singing_medusa: 1_300n,
  fish_mirror_seraph: 2_400n,
  fish_quantum_lamprey: 2_600n,
  fish_null_kraken: 5_300n,
  fish_paradox_sting: 5_600n,
  fish_prism_colossus: 12_000n,
  fish_aurora_sovereign: 12_800n,
});

/** O preço mínimo. Um peixe apanhado vale sempre alguma coisa. */
export const FISH_PRICE_FLOOR = 1n;

/**
 * **Quanto vale ESTE exemplar**, em Ouro inteiro.
 *
 * A única conta de preço que existe, e a que a cotação e a liquidação leem as
 * duas. Uma espécie desconhecida — um id retirado entre duas versões — vale o
 * piso: o registo existe e não pode desaparecer, mas também não pode ganhar um
 * preço que ninguém lhe deu.
 */
export function fishPriceGold(input: {
  /*
   * **Ativa ou retirada.** `RetiredFishSpecies` é a parte que as duas
   * partilham — id, raridade e peso de referência — e é tudo o que um preço
   * precisa. Exigir `FishSpecies` aqui obrigaria uma captura de uma espécie
   * retirada a inventar uma posição na progressão que ela já não tem.
   */
  readonly species: RetiredFishSpecies;
  readonly weightMg: number;
}): bigint {
  const base = BASE_VALUE_BY_SPECIES[input.species.id];
  if (base === undefined) {
    return FISH_PRICE_FLOOR;
  }
  const numerator =
    base *
    BigInt(FISH_RARITY_PRICE_BPS[input.species.rarity]) *
    BigInt(Math.max(1, Math.trunc(input.weightMg)));
  const denominator = FISH_PRICE_DENOMINATOR_BPS * BigInt(input.species.referenceWeightMg);
  const price = numerator / denominator;
  return price > FISH_PRICE_FLOOR ? price : FISH_PRICE_FLOOR;
}

/**
 * O mesmo, a partir de um id de espécie.
 *
 * A forma que a sala e a autoridade usam, porque o que elas guardam é o id. Uma
 * espécie que já não existe vale o piso, pela mesma razão.
 */
export function fishPriceGoldForSpecies(speciesId: string, weightMg: number): bigint {
  const species = findFishSpecies(speciesId);
  return species === null ? FISH_PRICE_FLOOR : fishPriceGold({ species, weightMg });
}

/**
 * **Duas provas que correm ao carregar o módulo**, e não numa suíte.
 *
 * Elas afirmam o que o contrato exige e o que nenhum tipo consegue exprimir: que
 * os multiplicadores crescem ESTRITAMENTE com a raridade, e que todas as vinte
 * espécies têm preço. Falhar aqui é falhar no arranque, que é onde uma tabela
 * mal editada tem de falhar — e não na primeira venda de um jogador.
 */
function assertFishPricesAreSound(): void {
  for (const species of [...FISH_SPECIES_LIST, ...RETIRED_FISH_SPECIES]) {
    if (BASE_VALUE_BY_SPECIES[species.id] === undefined) {
      throw new RangeError(`fish species ${species.id} has no base value`);
    }
  }
  /*
   * **O preço de referência cresce estritamente com a posição** (V28I.BK).
   *
   * No peso de referência o fator `peso / pesoReferência` vale um, portanto o
   * preço de referência é `valorBase x multiplicadorDaRaridade` e mais nada.
   * Esta é a prova que o catálogo não pode fazer sozinho: os valores-base vivem
   * aqui, e a monotonia por posição é uma propriedade dos dois juntos.
   *
   * As retiradas ficam de fora: elas não têm posição, e não estão na
   * progressão.
   */
  const byPosition = [...FISH_SPECIES_LIST].sort((a, b) => a.position - b.position);
  let previousPrice = 0n;
  for (const species of byPosition) {
    const reference = fishPriceGold({ species, weightMg: species.referenceWeightMg });
    if (reference <= previousPrice) {
      throw new RangeError(
        `fish reference price must grow strictly by position; ${species.id} at ${species.position} is ${reference} after ${previousPrice}`,
      );
    }
    previousPrice = reference;
  }
  let previous = 0;
  for (const rarity of RARITIES) {
    const value = FISH_RARITY_PRICE_BPS[rarity];
    if (value <= previous) {
      throw new RangeError(
        `fish price multipliers must grow strictly by rarity; ${rarity} is ${value} after ${previous}`,
      );
    }
    previous = value;
  }
}

assertFishPricesAreSound();
